import { DeliveryTypeCategory } from '@/features/orders/constants/app'
import { useCartKeyContext } from '@/features/pos/components/Cart/contexts/CartKey'
import { FormKeys } from '@/features/pos/components/OrderFinalization/form/constants'
import {
    FormContext,
    FormInput,
    FormOutput,
} from '@/features/pos/components/OrderFinalization/form/types'
import { logger } from '@/features/pos/logger'
import { PosMode } from '@/features/pos/types'
import StorageService from '@/services/storage'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

interface PersistAutomaticContainerRemovalsOptions {
    defaultDeliveryTypeCategory?: DeliveryTypeCategory
    posMode: PosMode
}

export const usePersistAutomaticContainerRemovals = ({
    defaultDeliveryTypeCategory,
    posMode,
}: PersistAutomaticContainerRemovalsOptions): boolean => {
    const methods = useFormContext<FormInput, FormContext, FormOutput>()
    const { cartKey, mode: cartKeyMode } = useCartKeyContext()
    const [hydratedIdentity, setHydratedIdentity] = useState<string | null>(null)
    const removals = useWatch({
        control: methods.control,
        name: FormKeys.AutomaticContainerRemovals,
    })
    const identity = useMemo(
        () =>
            posMode === cartKeyMode && cartKey && defaultDeliveryTypeCategory
                ? `${posMode}:${cartKey}:${defaultDeliveryTypeCategory}`
                : null,
        [cartKey, cartKeyMode, defaultDeliveryTypeCategory, posMode]
    )

    useEffect(() => {
        let active = true

        setHydratedIdentity(null)

        if (!identity || !cartKey) return () => void (active = false)

        const hydrate = async () => {
            try {
                const stored = await StorageService.getAutomaticContainerRemovals(posMode, cartKey)

                if (!active) return

                methods.setValue(FormKeys.AutomaticContainerRemovals, stored)
            } catch (error) {
                logger.errorOnly(error)

                if (!active) return

                methods.setValue(FormKeys.AutomaticContainerRemovals, [])
            } finally {
                if (active) setHydratedIdentity(identity)
            }
        }

        void hydrate()

        return () => void (active = false)
    }, [cartKey, identity, methods, posMode])

    useEffect(() => {
        if (!identity || !cartKey || hydratedIdentity !== identity) return

        void StorageService.setAutomaticContainerRemovals(posMode, cartKey, removals ?? []).catch(
            (error) => logger.errorOnly(error)
        )
    }, [cartKey, hydratedIdentity, identity, posMode, removals])

    return (
        !!defaultDeliveryTypeCategory &&
        posMode === cartKeyMode &&
        (!cartKey || hydratedIdentity === identity)
    )
}
